import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit2, Trash2, Star, Save, X, Eye, EyeOff, User } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const AdminReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    review_text: "",
    rating: 5,
    source: "manual",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const sources = [
    { id: "facebook", label: "Facebook" },
    { id: "google", label: "Google" },
    { id: "tripadvisor", label: "TripAdvisor" },
    { id: "manual", label: "Manuell" },
  ];

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Kunne ikke laste anmeldelser");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: "",
      review_text: "",
      rating: 5,
      source: "manual",
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingReview(null);
    setShowForm(false);
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      customer_name: review.customer_name,
      review_text: review.review_text,
      rating: review.rating,
      source: review.source || "manual",
    });
    setImagePreview(review.image_url ? `${API.replace('/api', '')}${review.image_url}` : null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = new FormData();
      submitData.append("customer_name", formData.customer_name);
      submitData.append("review_text", formData.review_text);
      submitData.append("rating", formData.rating);
      submitData.append("source", formData.source);
      if (imageFile) {
        submitData.append("image", imageFile);
      }
      if (editingReview) {
        submitData.append("is_active", editingReview.is_active);
      }

      if (editingReview) {
        const token = localStorage.getItem("admin_token");
        await axios.put(`${API}/admin/reviews/${editingReview.id}`, submitData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Anmeldelse oppdatert!");
      } else {
        const token = localStorage.getItem("admin_token");
        await axios.post(`${API}/admin/reviews`, submitData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Anmeldelse lagt til!");
      }

      resetForm();
      fetchReviews();
    } catch (error) {
      console.error("Error saving review:", error);
      toast.error("Kunne ikke lagre anmeldelse");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Er du sikker på at du vil slette denne anmeldelsen?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Anmeldelse slettet!");
      fetchReviews();
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("Kunne ikke slette anmeldelse");
    }
  };

  const handleToggleVisibility = async (review) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(`${API}/admin/reviews/${review.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(review.is_active ? "Anmeldelse skjult" : "Anmeldelse synlig");
      fetchReviews();
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast.error("Kunne ikke oppdatere synlighet");
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${interactive ? 'cursor-pointer' : ''}`}
            style={{
              color: star <= rating ? '#d4af37' : '#ccc',
              fill: star <= rating ? '#d4af37' : 'transparent'
            }}
            onClick={interactive && onChange ? () => onChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  const getSourceBadge = (source) => {
    const colors = {
      facebook: { bg: '#1877f2', text: '#fff' },
      google: { bg: '#4285f4', text: '#fff' },
      tripadvisor: { bg: '#00af87', text: '#fff' },
      manual: { bg: '#8b4513', text: '#fff' },
    };
    const style = colors[source] || colors.manual;
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-semibold"
        style={{ background: style.bg, color: style.text }}
      >
        {sources.find(s => s.id === source)?.label || 'Manuell'}
      </span>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-12 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="indian-divider mb-2"
              >
                <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Admin</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
                style={{ color: '#d4af37' }}
                data-testid="admin-reviews-title"
              >
                Anmeldelser
              </motion.h1>
              <p className="mt-1 text-sm" style={{ color: '#f5e6d3' }}>
                Legg til ekte anmeldelser fra Facebook, Google eller manuelt
              </p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowForm(true); }}
              className="rounded-full btn-gold"
              data-testid="add-review-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Legg til Anmeldelse
            </Button>
          </div>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tips */}
        <div 
          className="mb-8 p-4 rounded-xl"
          style={{ background: '#ede1c8', border: '2px solid #d4af37' }}
        >
          <p className="text-sm" style={{ color: '#2d1810' }}>
            <strong>Tips:</strong> Kopier ekte anmeldelser fra din Facebook-side eller Google Business Profile og lim dem inn her. Velg riktig kilde for å vise ikonet.
          </p>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(45, 24, 16, 0.9)' }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              style={{ border: '3px solid #d4af37' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                  {editingReview ? "Rediger Anmeldelse" : "Legg til Anmeldelse"}
                </h2>
                <Button variant="ghost" onClick={resetForm} style={{ color: '#721111' }}>
                  <X className="h-6 w-6" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Customer Image */}
                <div className="text-center">
                  <div 
                    className="relative w-24 h-24 mx-auto rounded-full overflow-hidden cursor-pointer group"
                    style={{ background: '#ede1c8', border: '2px dashed #b27f47' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <User className="h-10 w-10" style={{ color: '#b27f47' }} />
                      </div>
                    )}
                    <div 
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'rgba(45, 24, 16, 0.7)' }}
                    >
                      <span className="text-white text-xs">Endre</span>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <p className="text-xs mt-2" style={{ color: '#8b4513' }}>Kundebilde (valgfritt)</p>
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Kundenavn *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                    placeholder="F.eks. Maria S."
                    className="indian-input mt-1"
                  />
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Vurdering *</Label>
                  <div className="mt-2">
                    {renderStars(formData.rating, true, (star) => setFormData({ ...formData, rating: star }))}
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Anmeldelsestekst *</Label>
                  <Textarea
                    value={formData.review_text}
                    onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                    required
                    rows={4}
                    placeholder="Skriv eller lim inn anmeldelsen her..."
                    className="indian-input mt-1"
                  />
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Kilde</Label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="indian-input w-full mt-1"
                  >
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>{source.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 rounded-full"
                    style={{ background: 'transparent', border: '2px solid #721111', color: '#721111' }}
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-maroon rounded-full transition-transform hover:scale-105"
                  >
                    <Save className="h-5 w-5 mr-2" />
                    {saving ? "Lagrer..." : "Lagre"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Reviews Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div 
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
              style={{ borderColor: '#721111', borderRightColor: 'transparent' }}
            ></div>
            <p className="mt-4" style={{ color: '#4a3728' }}>Laster anmeldelser...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl" style={{ border: '2px solid #b27f47' }}>
            <Star className="h-16 w-16 mx-auto mb-4" style={{ color: '#b27f47' }} />
            <p className="text-lg mb-2" style={{ color: '#4a3728' }}>Ingen anmeldelser ennå</p>
            <p className="text-sm mb-4" style={{ color: '#8b4513' }}>
              Legg til ekte anmeldelser fra Facebook eller Google
            </p>
            <Button 
              onClick={() => setShowForm(true)}
              className="btn-maroon rounded-full"
            >
              <Plus className="h-5 w-5 mr-2" />
              Legg til Første Anmeldelse
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${!review.is_active ? 'opacity-60' : ''}`}
                style={{ border: '2px solid #b27f47' }}
                data-testid={`review-card-${review.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0"
                    style={{ background: '#ede1c8', border: '2px solid #d4af37' }}
                  >
                    {review.image_url ? (
                      <img
                        src={`${API.replace('/api', '')}${review.image_url}`}
                        alt={review.customer_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-8 w-8" style={{ color: '#b27f47' }} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold" style={{ color: '#2d1810' }}>{review.customer_name}</h3>
                        {getSourceBadge(review.source)}
                      </div>
                      {!review.is_active && (
                        <span className="text-xs px-2 py-1 rounded-full" style={{ background: '#ef4444', color: '#fff' }}>
                          Skjult
                        </span>
                      )}
                    </div>
                    <div className="mt-1">{renderStars(review.rating)}</div>
                  </div>
                </div>

                <p className="text-sm mb-4 line-clamp-3" style={{ color: '#4a3728' }}>
                  "{review.review_text}"
                </p>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleEdit(review)}
                    className="flex-1 rounded-full"
                    style={{ background: '#d4af37', color: '#2d1810' }}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Rediger
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleToggleVisibility(review)}
                    className="rounded-full"
                    style={{ 
                      background: 'transparent', 
                      border: '1px solid #721111', 
                      color: '#721111' 
                    }}
                    title={review.is_active ? "Skjul" : "Vis"}
                  >
                    {review.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(review.id)}
                    className="rounded-full"
                    style={{ background: '#ef4444', color: 'white' }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminReviewsPage;
